import { useCallback, useEffect, useState } from "react";
import { isValidIranianMobile, type NextActivity, type Roster, type StudentSummary } from "@talent/protocol";
import { ClassError, addStudent, fetchRoster } from "../class-api.js";
import { COPY, fill, taskLabel } from "../copy.js";
import { digitsOnly, fa, faDate } from "../format.js";
import { Creature } from "../creature.js";
import { useClasses } from "./class-context.js";
import { ScreenHeader } from "./screen-header.js";

/**
 * The class roster — the teacher's home.
 *
 * Every row answers the two questions the loop exists to answer: what has this child been doing, and
 * what should they do next. The suggestion carries **the reason it is being made**, because a
 * recommendation a teacher cannot interrogate is one they override once and then stop reading — and
 * at that point the loop is broken at its last step (ADR-011).
 *
 * **Rebuilt because the first version read as a list of paragraphs.** Each row was three stacked lines
 * of similar-weight text, so scanning twenty children meant reading sixty lines to find the one who has
 * not played. Now the shape carries the meaning: a disc with the child's initial anchors the row, the
 * name is the only large thing in it, and what is next is a chip — coloured when a teacher set it,
 * quiet when the system chose. A teacher looking for who needs attention sees it without reading.
 */

const CARD = "rounded-lg bg-card shadow-card";
const FIELD =
  "w-full rounded-md border border-edge bg-card px-md py-sm text-body outline-none focus-visible:border-grape";
const PRIMARY =
  "min-h-touch w-full rounded-pill bg-grape px-lg py-md text-heading text-card shadow-tint-grape disabled:opacity-60";

const REASON_LABEL: Record<NextActivity["reason"], string> = {
  assigned: COPY.teacher.roster.reasonAssigned,
  unfinished: COPY.teacher.roster.reasonUnfinished,
  progression: COPY.teacher.roster.reasonProgression,
  starting: COPY.teacher.roster.reasonStarting,
  "all-caught-up": COPY.teacher.roster.reasonCaughtUp,
};

type State =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "ready"; roster: Roster };

export function RosterScreen({
  onOpenStudent,
  onOpenGoals,
  onOpenCompare,
  onOpenPreview,
  onOpenGames,
  onOpenClasses,
  onOpenProfile,
  onSignedOut,
}: {
  onOpenStudent: (studentId: string) => void;
  onOpenGoals: () => void;
  onOpenCompare: () => void;
  onOpenPreview: () => void;
  /** Which games this class may play. Everything is shut until a teacher opens it (ADR-014). */
  onOpenGames: () => void;
  /** Adding, renaming, closing and choosing a class all live on their own screen. */
  onOpenClasses: () => void;
  /** The teacher's own page, which is also where signing out lives. */
  onOpenProfile: () => void;
  /** A session can expire while the screen is open; the shell decides where that goes. */
  onSignedOut: () => void;
}) {
  const { current, allowance, reload: reloadClasses } = useClasses();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [adding, setAdding] = useState(false);

  const classId = current?.classId ?? null;

  /**
   * Whether another child is on offer, counted over every class rather than this one.
   *
   * Unknown counts as yes: the server is the rule, and a button hidden on a guess leaves a teacher with
   * no way to learn why. When it IS known to be full the form is replaced by the sentence explaining it,
   * because a control whose only outcome is a refusal is worse than none — the same reasoning that hides
   * "close class" on a teacher's last one.
   */
  const roomForStudent = !allowance || allowance.students < allowance.studentLimit;

  const load = useCallback(async () => {
    if (!classId) return;
    try {
      setState({ kind: "ready", roster: await fetchRoster(classId) });
    } catch (err) {
      if (err instanceof ClassError && err.failure === "unauthenticated") {
        onSignedOut();
        return;
      }
      setState({
        kind: "failed",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [classId, onSignedOut]);

  // Re-runs when the chosen class changes, which is the whole mechanism behind the switcher: nothing
  // else has to know a switch happened.
  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") {
    return <p className="p-lg text-center text-muted">{COPY.teacher.loading}</p>;
  }
  if (state.kind === "failed") {
    return (
      <p className="p-lg text-center text-muted">
        {fill(COPY.teacher.failed, { message: state.message })}
      </p>
    );
  }

  const { roster } = state;
  const empty = roster.students.length === 0;

  return (
    <div className="mx-auto min-h-full max-w-[560px] px-lg pt-lg pb-xxl">
      {/*
        * The class is the heading, and the teacher's own name is the small line under it.
        *
        * After the first visit, "which class am I in" is the only question this header answers — and
        * it is the roster's root screen, so there is nothing to go back to. Everything a teacher does
        * TO a class is one tap away rather than folded into the title.
        */}
      <ScreenHeader
        title={current?.name ?? COPY.teacher.roster.title}
        {...(roster.teacherFirstName
          ? { subtitle: fill(COPY.teacher.roster.greeting, { name: roster.teacherFirstName }) }
          : {})}
        action={
          /*
            * The two doors that are about the teacher rather than the class: their classes, and their
            * account. Both are discs so the header stays one row whatever the class is called.
            */
          <span className="flex shrink-0 gap-sm">
            <HeaderButton onClick={onOpenClasses} label={COPY.teacher.classes.manage} mark="stack" />
            <HeaderButton onClick={onOpenProfile} label={COPY.teacher.profile.open} mark="person" />
          </span>
        }
      />

      {/*
        * The rest of the teacher's surface, as three tiles with drawn marks.
        *
        * The goals grid and the comparison are hidden while the roster is empty rather than disabled:
        * both would open on a screen explaining that it is empty, which is a worse answer than not
        * offering the door.
        *
        * **The game preview is always shown**, and that is the difference: a teacher deciding whether
        * this tool is worth setting up a class for wants to see the games first. Requiring them to
        * register a child before they can look is the wrong order.
        */}
      <nav className="mb-lg grid grid-cols-2 gap-sm">
        {/*
          * Which games the class may play comes FIRST, and is shown even when the roster is empty.
          *
          * Everything is shut by default (ADR-014), so this is the switch a new class needs before
          * anything else — a teacher who registers three children and finds an empty shelf has been
          * left with no way to work out why.
          */}
        <Tile
          label={COPY.teacher.games.title}
          accent="mint"
          mark="switch"
          onClick={onOpenGames}
        />
        <Tile label={COPY.teacher.preview.title} accent="sky" mark="games" onClick={onOpenPreview} />
        {!empty && (
          <>
            <Tile
              label={COPY.teacher.goals.title}
              accent="grape"
              mark="target"
              onClick={onOpenGoals}
            />
            <Tile
              label={COPY.teacher.compare.title}
              accent="sunny"
              mark="bars"
              onClick={onOpenCompare}
            />
          </>
        )}
      </nav>

      {empty && !adding && (
        <div className={`${CARD} mb-lg flex flex-col items-center px-md py-lg text-center`}>
          <Creature kind="firefly" accent="sunny" size={72} />
          <p className="mt-md text-body">{COPY.teacher.roster.empty}</p>
          <p className="mt-sm text-small text-muted">{COPY.teacher.roster.emptyHint}</p>
        </div>
      )}

      {!empty && (
        <>
          <p className="mb-sm px-1 text-caption text-muted">
            {fill(COPY.teacher.roster.count, { count: fa(roster.students.length) })}
          </p>
          {/*
            * One card holding hairline-separated rows, rather than one card per child.
            *
            * Twenty separately shadowed cards is twenty things to look at; twenty rows in one card is
            * one list. The difference is most of why this screen felt heavy.
            */}
          <ul className={`${CARD} mb-lg overflow-hidden`}>
            {roster.students.map((student) => (
              <li key={student.studentId} className="border-b border-edge last:border-b-0">
                <StudentRow student={student} onOpen={() => onOpenStudent(student.studentId)} />
              </li>
            ))}
          </ul>
        </>
      )}

      {adding ? (
        <AddStudent
          classId={classId}
          onCancel={() => setAdding(false)}
          onAdded={async () => {
            setAdding(false);
            await Promise.all([load(), reloadClasses()]);
          }}
        />
      ) : roomForStudent ? (
        <button className={PRIMARY} onClick={() => setAdding(true)}>
          {COPY.teacher.roster.add}
        </button>
      ) : (
        <p className={`${CARD} px-md py-md text-small text-muted`}>
          {fill(COPY.teacher.addStudent.limitFull, {
            limit: fa(allowance?.studentLimit ?? 0),
          })}
        </p>
      )}

    </div>
  );
}

/**
 * A round control in the header row.
 *
 * Drawn rather than lettered, and the same disc as the back button, so the top of every screen is one
 * family of shapes instead of a heading with words bolted to each end.
 */
function HeaderButton({
  onClick,
  label,
  mark,
}: {
  onClick: () => void;
  label: string;
  mark: "stack" | "person";
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className="-mt-1 grid size-11 place-items-center rounded-pill border border-edge bg-card text-ink shadow-card transition-colors active:bg-cream"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        aria-hidden
        fill="none"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {mark === "stack" ? (
          <>
            <path d="M12 3 21 7.5 12 12 3 7.5z" />
            <path d="M3 12.5 12 17l9-4.5M3 17 12 21.5 21 17" />
          </>
        ) : (
          <>
            <circle cx="12" cy="8" r="4" />
            <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
          </>
        )}
      </svg>
    </button>
  );
}

/**
 * One door out of the roster.
 *
 * The accents are literal per tile rather than looked up, for the reason in `accent.ts`: Tailwind finds
 * classes by scanning text, so a class assembled at runtime is a class that never reaches the
 * stylesheet and an element that renders unstyled with no error anywhere.
 */
function Tile({
  label,
  accent,
  mark,
  onClick,
}: {
  label: string;
  accent: "sky" | "mint" | "sunny" | "grape";
  mark: "games" | "target" | "bars" | "switch";
  onClick: () => void;
}) {
  const wash =
    accent === "sky"
      ? "bg-sky/12"
      : accent === "mint"
        ? "bg-mint/16"
        : accent === "grape"
          ? "bg-grape/14"
          : "bg-sunny/16";
  const ink =
    accent === "sky"
      ? "text-sky"
      : accent === "mint"
        ? "text-mint"
        : accent === "grape"
          ? "text-grape"
          : "text-sunny";

  return (
    <button
      onClick={onClick}
      className={`${CARD} flex min-h-touch flex-col items-center justify-center gap-1 px-sm py-md`}
    >
      <span className={`grid size-9 place-items-center rounded-pill ${wash} ${ink}`}>
        <TileMark mark={mark} />
      </span>
      <span className="text-caption">{label}</span>
    </button>
  );
}

/** Drawn rather than lettered: an icon needs no translating and no font to have loaded. */
function TileMark({ mark }: { mark: "games" | "target" | "bars" | "switch" }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      {mark === "games" && (
        <>
          <rect x="3" y="3" width="7.5" height="7.5" rx="2" />
          <rect x="13.5" y="3" width="7.5" height="7.5" rx="2" />
          <rect x="3" y="13.5" width="7.5" height="7.5" rx="2" />
          <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2" />
        </>
      )}
      {mark === "target" && (
        <>
          <circle cx="12" cy="12" r="8.5" />
          <circle cx="12" cy="12" r="3.5" />
        </>
      )}
      {mark === "bars" && <path d="M5 19V11M12 19V5M19 19v-6" />}
      {mark === "switch" && (
        <>
          <rect x="2.5" y="7" width="19" height="10" rx="5" />
          <circle cx="16.5" cy="12" r="2.6" fill="currentColor" />
        </>
      )}
    </svg>
  );
}

function StudentRow({
  student,
  onOpen,
}: {
  student: StudentSummary;
  onOpen: () => void;
}) {
  const name = [student.firstName, student.lastName].filter(Boolean).join(" ");
  const activity = student.next.taskId ? taskLabel(student.next.taskId) : null;
  /**
   * A goal the teacher set reads differently from one the system chose.
   *
   * The same chip in the same place either way, coloured only for `assigned` — a teacher scanning the
   * list wants to see their own instructions standing out from the recommender's, and a second row of
   * text saying which is which would be the thing they stop reading.
   */
  const mine = student.next.reason === "assigned";

  return (
    <button onClick={onOpen} className="flex w-full items-center gap-md px-md py-md text-start">
      <Initial name={student.firstName ?? name} played={student.lastPlayedAt !== null} />

      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-sm">
          <span className="truncate text-heading">{name}</span>
          {/* Never-played is the one status worth colouring: it is the only one a teacher must act on. */}
          <span
            className={`shrink-0 text-caption ${student.lastPlayedAt ? "text-muted" : "text-watermelon"}`}
          >
            {student.lastPlayedAt
              ? faDate(student.lastPlayedAt)
              : COPY.teacher.roster.neverPlayed}
          </span>
        </span>

        <span className="mt-1 flex flex-wrap items-center gap-x-sm gap-y-1">
          <span
            className={`rounded-pill px-sm py-px text-caption ${
              mine ? "bg-grape/14 text-ink" : "bg-cream text-muted"
            }`}
          >
            {activity
              ? fill(COPY.teacher.roster.nextUp, { activity })
              : REASON_LABEL[student.next.reason]}
          </span>
          {/* The reason travels with the suggestion, always. */}
          {activity && (
            <span className="text-caption text-muted">{REASON_LABEL[student.next.reason]}</span>
          )}
        </span>

        <span className="mt-1 block text-caption text-muted">
          {fill(COPY.teacher.roster.mastered, { count: fa(student.masteredObjectives) })}
        </span>
      </span>

      <Chevron />
    </button>
  );
}

/**
 * The child's first letter in a disc.
 *
 * A picture would be better and there is no picture: nothing here collects a photograph of a
 * five-year-old, and it should not. A letter is enough to make a row findable at a glance, which is all
 * this is for. Ringed rather than filled when the child has never played, so the list shows who has not
 * started without a second line of text saying so.
 */
function Initial({ name, played }: { name: string; played: boolean }) {
  return (
    <span
      className={`grid size-11 shrink-0 place-items-center rounded-pill text-heading ${
        played ? "bg-grape/14 text-ink" : "border-2 border-dashed border-edge text-muted"
      }`}
      aria-hidden
    >
      {[...name][0] ?? ""}
    </span>
  );
}

/**
 * An SVG, not a character.
 *
 * `‹` is in the Unicode bidi-mirroring set, so inside an RTL element the browser flips it and the glyph
 * points the opposite way from the one written. An SVG has no bidi behaviour.
 */
function Chevron() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      aria-hidden
      className="shrink-0 text-muted"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 5l-7 7 7 7" />
    </svg>
  );
}

function AddStudent({
  classId,
  onAdded,
  onCancel,
}: {
  classId: string | null;
  onAdded: () => Promise<void>;
  onCancel: () => void;
}) {
  /** "confirm" holds the entered name and phone still, so a mistake is one edit away, not a retype. */
  const [step, setStep] = useState<"details" | "confirm">("details");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!classId) return;
    setBusy(true);
    setError(null);
    try {
      await addStudent(classId, { firstName, lastName, phone });
      await onAdded();
    } catch (err) {
      const refusal = err instanceof ClassError ? err : null;
      setError(
        refusal?.failure === "phone-taken"
          ? COPY.teacher.addStudent.taken
          : refusal?.failure === "bad-phone"
            ? COPY.teacher.addStudent.badPhone
            : /*
               * Reachable although the button is hidden when the allowance is known to be full: the count
               * this screen holds can be seconds old, and the same account open on another device can take
               * the last place. The number in the message comes from the refusal, not from the screen.
               */
              refusal?.failure === "student-limit"
              ? fill(COPY.teacher.addStudent.limitFull, { limit: fa(refusal.limit ?? 0) })
              : COPY.enter.failed,
      );
      // Whatever went wrong, the fix is something to edit, not something to re-confirm.
      setStep("details");
    } finally {
      setBusy(false);
    }
  };

  const complete = firstName.trim() && lastName.trim() && isValidIranianMobile(phone);

  if (step === "confirm") {
    return (
      <ConfirmStudent
        fullName={[firstName, lastName].filter((part) => part.trim()).join(" ")}
        phone={phone}
        busy={busy}
        error={error}
        onConfirm={() => void submit()}
        onEdit={() => setStep("details")}
      />
    );
  }

  return (
    <form
      className={`${CARD} flex flex-col gap-md px-md py-md`}
      onSubmit={(e) => {
        e.preventDefault();
        if (complete && !busy) {
          // A refusal from the last attempt belongs to the details that caused it, not to the
          // fresh confirm screen an edited retry is about to show.
          setError(null);
          setStep("confirm");
        }
      }}
    >
      <h2 className="text-title">{COPY.teacher.addStudent.title}</h2>

      {/* Name and surname share a row: on a phone, two short fields side by side is one glance. */}
      <div className="flex gap-sm">
        <label className="flex min-w-0 flex-1 flex-col gap-1 text-small text-muted">
          {COPY.teacher.addStudent.firstName}
          <input
            className={FIELD}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
          />
        </label>

        <label className="flex min-w-0 flex-1 flex-col gap-1 text-small text-muted">
          {COPY.teacher.addStudent.lastName}
          <input
            className={FIELD}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
          />
        </label>
      </div>

      <label className="flex flex-col gap-1 text-small text-muted">
        {COPY.teacher.addStudent.phone}
        <input
          className={`${FIELD} text-center tracking-[2px]`}
          value={phone}
          // Folded to Latin as it is typed, so a Persian keyboard's ۰۹۱۲… is accepted rather than
          // silently dropped — the same treatment every other number field here gets.
          onChange={(e) => setPhone(digitsOnly(e.target.value).slice(0, 13))}
          inputMode="numeric"
          placeholder={COPY.enter.phonePlaceholder}
          autoComplete="off"
          dir="ltr"
        />
      </label>
      {/* Said here rather than left to be discovered: a teacher typing a child's number needs to
          know it is the number that child will sign in with. */}
      <p className="text-caption text-muted">{COPY.teacher.addStudent.phoneNote}</p>

      {error && <p className="text-small text-wrong">{error}</p>}

      <button type="submit" className={PRIMARY} disabled={!complete || busy}>
        {COPY.teacher.addStudent.next}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="py-sm text-small text-muted"
      >
        {COPY.teacher.addStudent.cancel}
      </button>
    </form>
  );
}

/**
 * One last look before the account exists and the SMS goes out.
 *
 * The number on this screen is the number a stranger's phone rings on if it was typed wrong, and
 * there is no undo once that message has sent — this is the step that catches it. Shown large and
 * alone, because the mistake this exists for is made by someone who has typed the same eleven digits
 * fifty times today and is no longer reading the field, only the shape of it.
 */
function ConfirmStudent({
  fullName,
  phone,
  busy,
  error,
  onConfirm,
  onEdit,
}: {
  fullName: string;
  phone: string;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onEdit: () => void;
}) {
  return (
    <section className={`${CARD} flex flex-col gap-md px-md py-md`}>
      <h2 className="text-title">{COPY.teacher.addStudent.confirmTitle}</h2>
      <p className="text-small text-muted">{COPY.teacher.addStudent.confirmWarning}</p>

      <div className="flex flex-col gap-md rounded-md bg-cream px-md py-md">
        <div>
          <p className="text-caption text-muted">{COPY.teacher.addStudent.confirmNameLabel}</p>
          <p className="truncate text-display text-ink">{fullName}</p>
        </div>
        <div>
          <p className="text-caption text-muted">{COPY.teacher.addStudent.phone}</p>
          <p className="text-hero text-ink tracking-[2px]" dir="ltr">
            {phone}
          </p>
        </div>
      </div>

      {error && <p className="text-small text-wrong">{error}</p>}

      <button type="button" className={PRIMARY} disabled={busy} onClick={onConfirm}>
        {busy ? COPY.enter.working : COPY.teacher.addStudent.confirmSave}
      </button>
      <button
        type="button"
        onClick={onEdit}
        disabled={busy}
        className="py-sm text-small text-muted"
      >
        {COPY.teacher.addStudent.edit}
      </button>
    </section>
  );
}
